import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "../api";

const COLUMNS = [
  { key: "considering", labelKey: "whatsNext.col.considering" },
  { key: "up_next", labelKey: "whatsNext.col.up_next" },
  { key: "in_progress", labelKey: "whatsNext.col.in_progress" },
  { key: "shipped", labelKey: "whatsNext.col.shipped" },
];

export default function RoadmapAdmin({ t }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDesc, setDraftDesc] = useState("");
  const draggedRef = useRef(null);

  const load = async () => {
    try { const r = await api.get("/admin/whats-next"); setItems(r.data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = async () => {
    if (!draftTitle.trim()) return;
    try {
      await api.post("/admin/whats-next", { title: draftTitle, description: draftDesc, status: "considering" });
      setDraftTitle(""); setDraftDesc(""); setAdding(false);
      await load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/whats-next/${id}`, { status }); await load(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("whatsNext.confirmDelete"))) return;
    try { await api.delete(`/admin/whats-next/${id}`); await load(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onDragStart = (e, item) => { draggedRef.current = item; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = async (e, status) => {
    e.preventDefault();
    const dragged = draggedRef.current;
    if (!dragged) return;
    draggedRef.current = null;
    if (dragged.status === status) return;
    await updateStatus(dragged.id, status);
  };

  if (!items) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div data-testid="admin-roadmap">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
        <p className="tb-muted" style={{ margin: 0, fontSize: "0.86rem", maxWidth: 600 }}>{t("whatsNext.adminHint")}</p>
        {!adding && (
          <button className="tb-primary-btn" onClick={() => setAdding(true)} data-testid="roadmap-add-btn" style={{ padding: "8px 16px", fontSize: "0.85rem" }}>
            + {t("whatsNext.addItem")}
          </button>
        )}
      </div>

      {adding && (
        <div className="tb-block" style={{ marginBottom: 16 }} data-testid="roadmap-add-form">
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder={t("whatsNext.titlePlaceholder")}
            style={{ width: "100%", marginBottom: 8 }}
            data-testid="roadmap-add-title"
            autoFocus
          />
          <textarea
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            placeholder={t("whatsNext.descPlaceholder")}
            style={{ width: "100%", minHeight: 70 }}
            data-testid="roadmap-add-desc"
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="tb-ghost-btn" onClick={() => { setAdding(false); setDraftTitle(""); setDraftDesc(""); }} data-testid="roadmap-add-cancel">
              {t("common.cancel")}
            </button>
            <button type="button" className="tb-primary-btn" onClick={addItem} data-testid="roadmap-add-save" disabled={!draftTitle.trim()}>
              {t("common.save")}
            </button>
          </div>
        </div>
      )}

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="tb-roadmap-board" data-testid="roadmap-board">
        {COLUMNS.map((col) => {
          const colItems = items.filter((i) => i.status === col.key);
          return (
            <div
              key={col.key}
              className="tb-roadmap-col"
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, col.key)}
              data-testid={`roadmap-col-${col.key}`}
            >
              <div className="tb-roadmap-col-head">
                <strong>{t(col.labelKey)}</strong>
                <span className="tb-muted">{colItems.length}</span>
              </div>
              <div className="tb-roadmap-col-body">
                {colItems.length === 0 && <div className="tb-roadmap-empty">{t("whatsNext.emptyCol")}</div>}
                {colItems.map((it) => (
                  <div
                    key={it.id}
                    className="tb-roadmap-card"
                    draggable
                    onDragStart={(e) => onDragStart(e, it)}
                    data-testid={`roadmap-item-${it.id}`}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                      <strong>{it.title}</strong>
                      <button type="button" className="tb-roadmap-del" onClick={() => remove(it.id)} aria-label="Delete" data-testid={`roadmap-del-${it.id}`}>×</button>
                    </div>
                    {it.description && <p>{it.description.length > 200 ? it.description.slice(0, 200) + "…" : it.description}</p>}
                    {(it.source_feedback_ids || []).length > 0 && (
                      <span className="tb-muted" style={{ fontSize: "0.72rem" }}>
                        💬 {it.source_feedback_ids.length} {t("whatsNext.linkedFeedback")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
